import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Search,
  UserPlus,
  Plus,
  Trash2,
  X,
  MapPin,
  Mail,
  Phone,
  Briefcase,
  Filter,
  ChevronDown,
  Users,
  RefreshCw,
  AlertTriangle,
  RotateCcw,
  Archive,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PersonnelList() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [personnel, setPersonnel] = useState([]);
  const [search, setSearch] = useState('');
  const [divisionFilter, setDivisionFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [disciplineFilter, setDisciplineFilter] = useState('');
  const [scope, setScope] = useState('active_staff');
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showRecycleBin, setShowRecycleBin] = useState(false);
  const [deletedList, setDeletedList] = useState([]);
  const [binLoading, setBinLoading] = useState(false);
  const [binBusyId, setBinBusyId] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [duplicateWarning, setDuplicateWarning] = useState(null);
  const [migrating, setMigrating] = useState(false);
  const [divisions, setDivisions] = useState([]);
  const [types, setTypes] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [positionTypes, setPositionTypes] = useState([]);
  const [stats, setStats] = useState(null);
  const [newPerson, setNewPerson] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    city: '',
    state: '',
    division: 'Therapy',
    personnel_type: 'Contractor',
    discipline: '',
    status: 'Active',
    job_title: '',
    hire_date: '',
  });

  useEffect(() => {
    fetchMetadata();
    fetchPersonnel();
    fetchStats();
  }, []);

  useEffect(() => {
    fetchPersonnel();
  }, [divisionFilter, typeFilter, statusFilter, disciplineFilter, scope]);

  const fetchMetadata = async () => {
    try {
      const [divRes, typeRes, statusRes, posRes] = await Promise.all([
        axios.get(`${API}/personnel/divisions`),
        axios.get(`${API}/personnel/types`),
        axios.get(`${API}/personnel/statuses`),
        axios.get(`${API}/ats/position-types`),
      ]);
      setDivisions(divRes.data.divisions);
      setTypes(typeRes.data.types);
      setStatuses(statusRes.data.statuses);
      setPositionTypes(posRes.data.position_types);
    } catch (error) {
      console.error('Failed to load metadata:', error);
    }
  };

  const fetchPersonnel = async () => {
    try {
      const params = {};
      // Top-card scope drives the base filter
      if (scope === 'active_staff') params.active_staff = true;
      else if (scope === 'inactive') params.inactive = true;
      else if (scope === 'employees') { params.personnel_type = 'Employee'; params.status = 'Active'; }
      else if (scope === 'contractors') { params.personnel_type = 'Contractor'; params.status = 'Active'; }
      // Granular Filters panel (overrides / adds to the scope)
      if (divisionFilter) params.division = divisionFilter;
      if (typeFilter) params.personnel_type = typeFilter;
      if (statusFilter) params.status = statusFilter;
      if (disciplineFilter) params.discipline = disciplineFilter;
      if (search) params.search = search;
      const response = await axios.get(`${API}/personnel`, { params });
      // Sort by last name, then first name
      const sorted = response.data.sort((a, b) => {
        const lastNameCompare = (a.last_name || '').localeCompare(b.last_name || '');
        if (lastNameCompare !== 0) return lastNameCompare;
        return (a.first_name || '').localeCompare(b.first_name || '');
      });
      setPersonnel(sorted);
    } catch (error) {
      toast.error('Failed to load personnel');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/personnel/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const handleSearch = () => {
    fetchPersonnel();
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (isAdmin && params.get('recycle') === '1') {
      openRecycleBin();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const openRecycleBin = async () => {
    setShowRecycleBin(true);
    setBinLoading(true);
    try {
      const res = await axios.get(`${API}/personnel-recycle-bin`);
      setDeletedList(res.data || []);
    } catch (error) {
      toast.error('Failed to load Recycle Bin');
    } finally {
      setBinLoading(false);
    }
  };

  const handleRestore = async (person) => {
    const name = `${person.first_name || ''} ${person.last_name || ''}`.trim();
    setBinBusyId(person.id);
    try {
      const res = await axios.post(`${API}/personnel/${person.id}/restore`);
      toast.success(res.data.message || `Restored ${name}`);
      setDeletedList((prev) => prev.filter((p) => p.id !== person.id));
      fetchPersonnel();
      fetchStats();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to restore');
    } finally {
      setBinBusyId(null);
    }
  };

  const handlePurge = async (person) => {
    const name = `${person.first_name || ''} ${person.last_name || ''}`.trim();
    if (!window.confirm(`Permanently delete ${name}? This CANNOT be undone.`)) return;
    setBinBusyId(person.id);
    try {
      await axios.delete(`${API}/personnel-recycle-bin/${person.id}`);
      toast.success(`Permanently deleted ${name}`);
      setDeletedList((prev) => prev.filter((p) => p.id !== person.id));
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to delete');
    } finally {
      setBinBusyId(null);
    }
  };


  const handleMigrateTherapists = async () => {
    if (!window.confirm('This will migrate all therapist profiles to personnel records. Continue?')) return;
    setMigrating(true);
    try {
      const response = await axios.post(`${API}/personnel/migrate-therapists`);
      toast.success(response.data.message);
      fetchPersonnel();
      fetchStats();
    } catch (error) {
      toast.error('Failed to migrate therapists');
    } finally {
      setMigrating(false);
    }
  };

  const handleAddPerson = async (e) => {
    e.preventDefault();
    if (!newPerson.first_name || !newPerson.last_name) {
      toast.error('First name and last name are required');
      return;
    }

    // Check for duplicates first (skip if already confirmed)
    if (!duplicateWarning) {
      try {
        const res = await axios.post(`${API}/personnel/check-duplicate`, {
          first_name: newPerson.first_name,
          last_name: newPerson.last_name,
          email: newPerson.email,
        });
        if (res.data.duplicates && res.data.duplicates.length > 0) {
          setDuplicateWarning(res.data.duplicates);
          return;
        }
      } catch (err) {
        // If check fails, proceed with creation
      }
    }

    try {
      const response = await axios.post(`${API}/personnel`, newPerson);
      toast.success('Personnel added successfully');
      setShowAddModal(false);
      setDuplicateWarning(null);
      setNewPerson({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        city: '',
        state: '',
        division: 'Therapy',
        personnel_type: 'Contractor',
        discipline: '',
        status: 'Active',
        job_title: '',
        hire_date: '',
      });
      navigate(`/personnel/${response.data.id}`);
    } catch (error) {
      toast.error('Failed to add personnel');
    }
  };

  const handleDeletePerson = async (e, personId, name) => {
    e.stopPropagation();
    if (!window.confirm(`Are you sure you want to delete ${name}?`)) return;
    try {
      await axios.delete(`${API}/personnel/${personId}`);
      toast.success('Personnel deleted successfully');
      fetchPersonnel();
      fetchStats();
    } catch (error) {
      toast.error('Failed to delete personnel');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active':
        return 'bg-green-100 text-green-800';
      case 'Onboarding':
        return 'bg-blue-100 text-blue-800';
      case 'Inactive':
        return 'bg-gray-100 text-gray-800';
      case 'On Leave':
        return 'bg-yellow-100 text-yellow-800';
      case 'Terminated':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getDivisionColor = (division) => {
    switch (division) {
      case 'Therapy':
        return 'bg-blue-100 text-blue-800';
      case 'Administration':
        return 'bg-purple-100 text-purple-800';
      case 'Operations':
        return 'bg-orange-100 text-orange-800';
      case 'Managing Members':
        return 'bg-pink-100 text-pink-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-4xl font-semibold text-foreground tracking-tight">
            Personnel
          </h1>
          <p className="text-muted-foreground mt-2 leading-relaxed">
            Manage employees and contractors
          </p>
        </div>
        <div className="flex gap-2">
          {isAdmin && (
            <Button
              variant="outline"
              onClick={openRecycleBin}
              data-testid="recycle-bin-btn"
            >
              <Archive size={16} className="mr-2" />
              Recycle Bin
            </Button>
          )}
          <Button
            variant="outline"
            onClick={handleMigrateTherapists}
            disabled={migrating}
            data-testid="migrate-btn"
          >
            <RefreshCw size={16} className={`mr-2 ${migrating ? 'animate-spin' : ''}`} />
            {migrating ? 'Migrating...' : 'Migrate Therapists'}
          </Button>
          <Button
            onClick={() => setShowAddModal(true)}
            className="gap-2"
            data-testid="add-personnel-btn"
          >
            <Plus size={18} />
            Add Personnel
          </Button>
        </div>
      </div>

      {/* Filter Cards - Always visible at top */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sticky top-0 z-10 bg-background pb-4">
          <Card 
            className={`p-4 cursor-pointer transition-all hover:shadow-md ${
              scope === 'active_staff' ? 'ring-2 ring-primary bg-primary/5' : ''
            }`}
            onClick={() => { setScope('active_staff'); setTypeFilter(''); setStatusFilter(''); }}
            data-testid="filter-all"
          >
            <div className="text-2xl font-bold">{stats.active_staff ?? 0}</div>
            <div className="text-sm text-muted-foreground">All Personnel</div>
          </Card>
          <Card 
            className={`p-4 cursor-pointer transition-all hover:shadow-md ${
              scope === 'employees' ? 'ring-2 ring-blue-500 bg-blue-50' : ''
            }`}
            onClick={() => { setScope('employees'); setTypeFilter(''); setStatusFilter(''); }}
            data-testid="filter-employees"
          >
            <div className="text-2xl font-bold text-blue-600">{stats.by_type_active?.Employee || 0}</div>
            <div className="text-sm text-muted-foreground">Employees</div>
          </Card>
          <Card 
            className={`p-4 cursor-pointer transition-all hover:shadow-md ${
              scope === 'contractors' ? 'ring-2 ring-orange-500 bg-orange-50' : ''
            }`}
            onClick={() => { setScope('contractors'); setTypeFilter(''); setStatusFilter(''); }}
            data-testid="filter-contractors"
          >
            <div className="text-2xl font-bold text-orange-600">{stats.by_type_active?.Contractor || 0}</div>
            <div className="text-sm text-muted-foreground">Contractors</div>
          </Card>
          <Card 
            className={`p-4 cursor-pointer transition-all hover:shadow-md ${
              scope === 'inactive' ? 'ring-2 ring-gray-500 bg-gray-50' : ''
            }`}
            onClick={() => { setScope('inactive'); setTypeFilter(''); setStatusFilter(''); }}
            data-testid="filter-inactive"
          >
            <div className="text-2xl font-bold text-gray-600">{stats.inactive ?? 0}</div>
            <div className="text-sm text-muted-foreground">Inactive</div>
          </Card>
        </div>
      )}

      <Card className="p-6">
        {/* Active Filter Indicator */}
        {(typeFilter || statusFilter || divisionFilter || disciplineFilter) && (
          <div className="flex items-center gap-2 mb-4 pb-4 border-b">
            <span className="text-sm text-muted-foreground">Showing:</span>
            {typeFilter && (
              <Badge variant="secondary" className="gap-1">
                {typeFilter}s
                <X 
                  size={12} 
                  className="cursor-pointer hover:text-destructive" 
                  onClick={() => setTypeFilter('')}
                />
              </Badge>
            )}
            {statusFilter && (
              <Badge variant="secondary" className="gap-1">
                {statusFilter}
                <X 
                  size={12} 
                  className="cursor-pointer hover:text-destructive" 
                  onClick={() => setStatusFilter('')}
                />
              </Badge>
            )}
            {divisionFilter && (
              <Badge variant="secondary" className="gap-1">
                {divisionFilter}
                <X 
                  size={12} 
                  className="cursor-pointer hover:text-destructive" 
                  onClick={() => setDivisionFilter('')}
                />
              </Badge>
            )}
            {disciplineFilter && (
              <Badge variant="secondary" className="gap-1">
                {disciplineFilter}
                <X 
                  size={12} 
                  className="cursor-pointer hover:text-destructive" 
                  onClick={() => setDisciplineFilter('')}
                />
              </Badge>
            )}
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => {
                setTypeFilter('');
                setStatusFilter('');
                setDivisionFilter('');
                setDisciplineFilter('');
              }}
              className="text-xs text-muted-foreground hover:text-foreground"
            >
              Clear all
            </Button>
          </div>
        )}
        
        {/* Search and Filters */}
        <div className="space-y-4 mb-6">
          <div className="flex gap-4">
            <div className="relative flex-1">
              <Search
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground"
                size={18}
              />
              <Input
                type="text"
                placeholder="Search by name, email, phone, job title..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className="gap-2"
              data-testid="toggle-filters"
            >
              <Filter size={16} />
              Filters
              <ChevronDown
                size={16}
                className={`transition-transform ${showFilters ? 'rotate-180' : ''}`}
              />
            </Button>
          </div>

          {showFilters && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 p-4 bg-muted/30 rounded-lg">
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">
                  Division
                </label>
                <select
                  className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
                  value={divisionFilter}
                  onChange={(e) => setDivisionFilter(e.target.value)}
                  data-testid="division-filter"
                >
                  <option value="">All Divisions</option>
                  {divisions.map((div) => (
                    <option key={div} value={div}>
                      {div}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">
                  Type
                </label>
                <select
                  className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  data-testid="type-filter"
                >
                  <option value="">All Types</option>
                  {types.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">
                  Status
                </label>
                <select
                  className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  data-testid="status-filter"
                >
                  <option value="">All Statuses</option>
                  {statuses.map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">
                  Discipline
                </label>
                <select
                  className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
                  value={disciplineFilter}
                  onChange={(e) => setDisciplineFilter(e.target.value)}
                  data-testid="discipline-filter"
                >
                  <option value="">All Disciplines</option>
                  {positionTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>

        {loading ? (
          <div className="text-center py-12 text-muted-foreground">
            Loading personnel...
          </div>
        ) : personnel.length === 0 ? (
          <div className="text-center py-12">
            <Users
              className="mx-auto mb-4 text-muted-foreground"
              size={48}
              strokeWidth={1.5}
            />
            <p className="text-muted-foreground mb-2">No personnel found</p>
            <p className="text-sm text-muted-foreground mb-4">
              Click "Migrate Therapists" to import existing therapist profiles
            </p>
            <Button
              onClick={() => setShowAddModal(true)}
              data-testid="empty-add-personnel"
            >
              <Plus size={16} className="mr-2" />
              Add Personnel
            </Button>
          </div>
        ) : (
          <div className="overflow-auto rounded-lg border border-border">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider p-4">
                    Name
                  </th>
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider p-4">
                    Contact
                  </th>
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider p-4">
                    Division
                  </th>
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider p-4">
                    Type
                  </th>
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider p-4">
                    Discipline
                  </th>
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider p-4">
                    Status
                  </th>
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider p-4">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {personnel.map((person) => (
                  <tr
                    key={person.id}
                    className="border-b border-border hover:bg-muted/20 transition-colors cursor-pointer"
                    onClick={() => navigate(`/personnel/${person.id}`)}
                    data-testid={`personnel-row-${person.id}`}
                  >
                    <td className="p-4">
                      <div className="font-medium">
                        {person.last_name}, {person.first_name}
                      </div>
                      {person.job_title && (
                        <div className="text-xs text-muted-foreground">
                          {person.job_title}
                        </div>
                      )}
                    </td>
                    <td className="p-4">
                      <div className="text-sm space-y-1">
                        {person.email && (
                          <div className="flex items-center gap-1 text-muted-foreground">
                            <Mail size={12} />
                            {person.email}
                          </div>
                        )}
                        {person.phone && (
                          <div className="flex items-center gap-1 text-muted-foreground">
                            <Phone size={12} />
                            {person.phone}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="p-4">
                      <Badge className={getDivisionColor(person.division)}>
                        {person.division}
                      </Badge>
                    </td>
                    <td className="p-4">
                      <span className="text-sm">{person.personnel_type}</span>
                    </td>
                    <td className="p-4">
                      <span className="text-sm">{person.discipline || '-'}</span>
                    </td>
                    <td className="p-4">
                      <Badge className={getStatusColor(person.status)}>
                        {person.status}
                      </Badge>
                    </td>
                    <td className="p-4">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        onClick={(e) =>
                          handleDeletePerson(
                            e,
                            person.id,
                            `${person.first_name} ${person.last_name}`
                          )
                        }
                        data-testid={`delete-personnel-${person.id}`}
                      >
                        <Trash2 size={16} />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Recycle Bin Modal (admin-only) */}
      {showRecycleBin && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="recycle-bin-modal">
          <Card className="w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-5 border-b">
              <div className="flex items-center gap-2">
                <Archive size={20} className="text-muted-foreground" />
                <div>
                  <h2 className="text-lg font-semibold">Recycle Bin</h2>
                  <p className="text-xs text-muted-foreground">Deleted personnel — restore or permanently remove</p>
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setShowRecycleBin(false)} data-testid="recycle-bin-close">
                <X size={18} />
              </Button>
            </div>
            <div className="overflow-y-auto p-5">
              {binLoading ? (
                <div className="py-12 flex justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" />
                </div>
              ) : deletedList.length === 0 ? (
                <div className="py-12 text-center text-muted-foreground" data-testid="recycle-bin-empty">
                  The Recycle Bin is empty. Deleted personnel will appear here and can be restored.
                </div>
              ) : (
                <div className="space-y-2">
                  {deletedList.map((p) => (
                    <div
                      key={p.id}
                      className="flex items-center justify-between gap-3 p-3 border rounded-lg hover:bg-muted/30"
                      data-testid={`deleted-person-${p.id}`}
                    >
                      <div className="min-w-0">
                        <div className="font-medium truncate">
                          {`${p.first_name || ''} ${p.last_name || ''}`.trim() || p.name || 'Unnamed'}
                          {p.personnel_type && <span className="ml-2 text-xs text-muted-foreground">{p.personnel_type}</span>}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          Deleted by {p.deleted_by_name || p.deleted_by || 'unknown'}
                          {p.deleted_at && ` · ${new Date(p.deleted_at).toLocaleString()}`}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Button
                          size="sm"
                          onClick={() => handleRestore(p)}
                          disabled={binBusyId === p.id}
                          data-testid={`restore-person-${p.id}`}
                        >
                          <RotateCcw size={14} className="mr-1" />
                          Restore
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => handlePurge(p)}
                          disabled={binBusyId === p.id}
                          data-testid={`purge-person-${p.id}`}
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Card>
        </div>
      )}


      {/* Add Personnel Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 overflow-y-auto py-8">
          <Card className="w-full max-w-2xl p-6 m-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">Add New Personnel</h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setShowAddModal(false); setDuplicateWarning(null); }}
              >
                <X size={18} />
              </Button>
            </div>
            <form onSubmit={handleAddPerson} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    First Name *
                  </label>
                  <Input
                    value={newPerson.first_name}
                    onChange={(e) =>
                      setNewPerson({ ...newPerson, first_name: e.target.value })
                    }
                    placeholder="First name"
                    data-testid="new-first-name"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Last Name *
                  </label>
                  <Input
                    value={newPerson.last_name}
                    onChange={(e) =>
                      setNewPerson({ ...newPerson, last_name: e.target.value })
                    }
                    placeholder="Last name"
                    data-testid="new-last-name"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Email
                  </label>
                  <Input
                    type="email"
                    value={newPerson.email}
                    onChange={(e) =>
                      setNewPerson({ ...newPerson, email: e.target.value })
                    }
                    placeholder="email@example.com"
                    data-testid="new-email"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Phone
                  </label>
                  <Input
                    value={newPerson.phone}
                    onChange={(e) =>
                      setNewPerson({ ...newPerson, phone: e.target.value })
                    }
                    placeholder="(555) 123-4567"
                    data-testid="new-phone"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    City
                  </label>
                  <Input
                    value={newPerson.city}
                    onChange={(e) =>
                      setNewPerson({ ...newPerson, city: e.target.value })
                    }
                    placeholder="City"
                    data-testid="new-city"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    State
                  </label>
                  <Input
                    value={newPerson.state}
                    onChange={(e) =>
                      setNewPerson({ ...newPerson, state: e.target.value })
                    }
                    placeholder="TX"
                    data-testid="new-state"
                  />
                </div>
              </div>

              <div className="border-t pt-4 mt-4">
                <h3 className="font-medium mb-3">Employment Details</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Division
                    </label>
                    <select
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      value={newPerson.division}
                      onChange={(e) =>
                        setNewPerson({ ...newPerson, division: e.target.value })
                      }
                      data-testid="new-division"
                    >
                      {divisions.map((div) => (
                        <option key={div} value={div}>
                          {div}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Type
                    </label>
                    <select
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      value={newPerson.personnel_type}
                      onChange={(e) =>
                        setNewPerson({ ...newPerson, personnel_type: e.target.value })
                      }
                      data-testid="new-type"
                    >
                      {types.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Discipline
                    </label>
                    <select
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      value={newPerson.discipline}
                      onChange={(e) =>
                        setNewPerson({ ...newPerson, discipline: e.target.value })
                      }
                      data-testid="new-discipline"
                    >
                      <option value="">Select...</option>
                      {positionTypes.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Status
                    </label>
                    <select
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      value={newPerson.status}
                      onChange={(e) =>
                        setNewPerson({ ...newPerson, status: e.target.value })
                      }
                      data-testid="new-status"
                    >
                      {statuses.map((status) => (
                        <option key={status} value={status}>
                          {status}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Job Title
                    </label>
                    <Input
                      value={newPerson.job_title}
                      onChange={(e) =>
                        setNewPerson({ ...newPerson, job_title: e.target.value })
                      }
                      placeholder="e.g., Physical Therapist"
                      data-testid="new-job-title"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Hire Date
                    </label>
                    <Input
                      type="date"
                      value={newPerson.hire_date}
                      onChange={(e) =>
                        setNewPerson({ ...newPerson, hire_date: e.target.value })
                      }
                      data-testid="new-hire-date"
                    />
                  </div>
                </div>
              </div>

              {/* Send Invitation + Role */}
              {newPerson.email && (
                <div className="space-y-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="pl_send_invitation"
                      checked={!!newPerson.send_invitation}
                      onChange={(e) => setNewPerson({ ...newPerson, send_invitation: e.target.checked })}
                      className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                      data-testid="pl-send-invitation-checkbox"
                    />
                    <div>
                      <label htmlFor="pl_send_invitation" className="text-sm font-medium text-blue-900 cursor-pointer">
                        Create login account &amp; send invitation
                      </label>
                      <p className="text-xs text-blue-700 mt-0.5">
                        They'll get a temporary password by email and can log in.
                      </p>
                    </div>
                  </div>
                  {newPerson.send_invitation && (
                    <div className="ml-7 pl-3 border-l-2 border-blue-300">
                      <label className="text-xs font-medium text-blue-900 mb-1 block">Access level</label>
                      <div className="grid grid-cols-2 gap-2">
                        <label
                          className={`flex items-center gap-2 p-2 border rounded cursor-pointer text-sm ${(newPerson.user_role || 'viewer') === 'viewer' ? 'border-blue-500 bg-white' : 'border-blue-200'}`}
                          data-testid="pl-role-viewer-option"
                        >
                          <input
                            type="radio"
                            name="pl_user_role"
                            value="viewer"
                            checked={(newPerson.user_role || 'viewer') === 'viewer'}
                            onChange={(e) => setNewPerson({ ...newPerson, user_role: e.target.value })}
                          />
                          <div className="flex-1">
                            <div className="font-medium">User</div>
                            <div className="text-[10px] text-muted-foreground">Standard access</div>
                          </div>
                        </label>
                        <label
                          className={`flex items-center gap-2 p-2 border rounded cursor-pointer text-sm ${newPerson.user_role === 'admin' ? 'border-blue-500 bg-white' : 'border-blue-200'}`}
                          data-testid="pl-role-admin-option"
                        >
                          <input
                            type="radio"
                            name="pl_user_role"
                            value="admin"
                            checked={newPerson.user_role === 'admin'}
                            onChange={(e) => setNewPerson({ ...newPerson, user_role: e.target.value })}
                          />
                          <div className="flex-1">
                            <div className="font-medium">Admin</div>
                            <div className="text-[10px] text-muted-foreground">Full access</div>
                          </div>
                        </label>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="flex justify-end gap-2 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => { setShowAddModal(false); setDuplicateWarning(null); }}
                >
                  Cancel
                </Button>
                <Button type="submit" data-testid="submit-new-personnel">
                  Add Personnel
                </Button>
              </div>

              {/* Duplicate Warning */}
              {duplicateWarning && duplicateWarning.length > 0 && (
                <div className="p-4 rounded-lg border border-amber-300 bg-amber-50" data-testid="duplicate-warning">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle size={18} className="text-amber-600" />
                    <span className="font-semibold text-amber-800">Possible duplicate found</span>
                  </div>
                  <div className="space-y-2 mb-3">
                    {duplicateWarning.map((dup, i) => (
                      <div key={i} className="text-sm text-amber-700 flex items-center gap-2 flex-wrap">
                        <span className="font-medium">{dup.first_name} {dup.last_name}</span>
                        {dup.email && <span className="text-amber-600">({dup.email})</span>}
                        <Badge className="text-[10px] bg-amber-100 text-amber-700">{dup.status || 'Unknown'}</Badge>
                        {dup.division && <span className="text-xs text-amber-600">{dup.division}</span>}
                        {dup.discipline && <span className="text-xs text-amber-600">- {dup.discipline}</span>}
                        {dup.hire_date && <span className="text-xs text-amber-600">Hired {dup.hire_date}</span>}
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-amber-600">Click "Add Personnel" again to create anyway.</p>
                </div>
              )}
            </form>
          </Card>
        </div>
      )}
    </div>
  );
}
